import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import { Home, List, Heart, Play, Pause, X, Disc } from 'lucide-react';
import './App.css';

// Import Pages
import HomePage from './pages/HomePage';
import MusicList from './pages/MusicList';
import SavedMusic from './pages/SavedMusic';

const musicData = [
  { id: 1, title: "Deep Healing", artist: "Silent Vibes", url: "assets/musiclists/healing.mp3" },
  { id: 2, title: "Night at the Museum", artist: "Ambient Pro", url: "assets/musiclists/museum.mp3" },
  { id: 3, title: "Old Ragtime Piano", artist: "Vintage Keys", url: "assets/musiclists/old-ragtime-piano.mp3" },
  { id: 4, title: "The Piano Roll", artist: "Classical Silent", url: "assets/musiclists/piano-roll.mp3" },
  { id: 5, title: "Reflective Silence", artist: "Zen Master", url: "assets/musiclists/reflective-silent.mp3" },
  { id: 6, title: "Silent Movie Piano", artist: "CineSound", url: "assets/musiclists/silent-movie-piano.mp3" },
  { id: 7, title: "Violet", artist: "Ethereal Sound", url: "assets/musiclists/violet.mp3" },
  { id: 8, title: "Silent Night", artist: "Holiday Chill", url: "assets/musiclists/silent-night.mp3" },
  { id: 9, title: "Silent Night Lights", artist: "Holiday Chill", url: "assets/musiclists/silent-night-lights.mp3" },
  { id: 10, title: "Silent Radiance", artist: "Solstice", url: "assets/musiclists/silent-radiance.mp3" }
];

function App() {
  // Global State
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [saved, setSaved] = useState(() => {
    const localData = localStorage.getItem('sl_favs');
    return localData ? JSON.parse(localData) : [];
  });

  const audioRef = useRef(null);

  // Persistence logic for Favorites
  useEffect(() => {
    localStorage.setItem('sl_favs', JSON.stringify(saved));
  }, [saved]);

  // Audio Control Logic
  const handlePlay = (track) => {
    if (currentTrack?.id === track.id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentTrack(track);
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.play().catch(err => {
        console.error("Playback failed:", err);
        setIsPlaying(false);
      });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, currentTrack]);

  return (
    <BrowserRouter>
      <div className="app-shell">
        {/* The Audio Engine */}
        <audio 
          ref={audioRef} 
          src={currentTrack?.url} 
          onEnded={() => setIsPlaying(false)} 
        />

        {/* 1. Global Header */}
        <header className="app-header">
          <div className="logo-wrapper">
            <div className="logo-box">
              <img src="assets/logo/slmusiclogo.png" alt="SL Music" />
            </div>
            <div className="brand-name">SL <span>Music</span></div>
          </div>
        </header>

        {/* 2. Page Routing with Prop Drilling */}
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route 
              path="/list" 
              element={
                <MusicList 
                  musicData={musicData} 
                  playTrack={handlePlay} 
                  saved={saved} 
                  setSaved={setSaved} 
                  currentTrackId={currentTrack?.id}
                  isPlaying={isPlaying}
                />
              } 
            />
            <Route 
              path="/saved" 
              element={
                <SavedMusic 
                  saved={saved} 
                  setSaved={setSaved} 
                  playTrack={handlePlay}
                  currentTrackId={currentTrack?.id}
                  isPlaying={isPlaying}
                />
              } 
            />
          </Routes>
        </main>

        {/* 3. Global Floating Player */}
        {currentTrack && (
          <div className="player-drawer fade-in">
            <div className="player-inner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', overflow: 'hidden' }}>
                <div className="player-art-mini">
                  <Disc className={isPlaying ? "spin" : ""} size={20} color="var(--accent)" />
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', whiteSpace: 'nowrap' }}>
                    {currentTrack.title}
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.7 }}>{currentTrack.artist}</div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <button 
                  onClick={() => setIsPlaying(!isPlaying)} 
                  className="player-main-btn"
                  style={{ 
                    background: 'var(--accent-grad)', border: 'none', borderRadius: '50%', 
                    width: '40px', height: '40px', color: 'white', display: 'flex', 
                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer' 
                  }}
                >
                  {isPlaying ? <Pause size={18} fill="white" /> : <Play size={18} fill="white" />}
                </button>
                <X 
                  size={20} 
                  onClick={() => { setCurrentTrack(null); setIsPlaying(false); }} 
                  style={{ cursor: 'pointer', opacity: 0.5 }} 
                />
              </div>
            </div>
          </div>
        )}

        {/* 4. Floating Capsule Navigation */}
        <nav className="mobile-nav">
          <NavLink to="/" className="nav-link" end>
            <Home size={22} />
            <span>Home</span>
          </NavLink>
          <NavLink to="/list" className="nav-link">
            <List size={22} />
            <span>Discover</span>
          </NavLink>
          <NavLink to="/saved" className="nav-link">
            <Heart size={22} />
            <span>Library</span>
          </NavLink>
        </nav>
      </div>
    </BrowserRouter>
  );
}

export default App;